import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X, Check, ShieldCheck, Loader2, Send } from "lucide-react";
import { PAYMENT_METHODS } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CheckoutModal = ({ plan, onClose }) => {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);

    const confirm = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/contact`, {
                name: form.name,
                email: form.email,
                subject: `Subscription — ${plan.name} (€${plan.price})`,
                message: form.message,
            });
            setDone(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not send your message. Please try again.");
        } finally {
            setSending(false);
        }
    };

    const inputCls =
        "w-full bg-[#0B0F17] border border-bn-line rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-bn-blue focus:ring-2 focus:ring-bn-blue/20 transition-all";

    return (
        <AnimatePresence>
            {plan && (
                <motion.div
                    data-testid="checkout-simulation-modal"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 40, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 40, scale: 0.96 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-[#0B0F17] border border-bn-blue/25 rounded-2xl p-6 sm:p-8 shadow-[0_40px_100px_rgba(0,0,0,0.7)]"
                    >
                        <button
                            data-testid="checkout-modal-close"
                            onClick={onClose}
                            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-lg border border-white/10 text-slate-400 hover:text-white hover:border-bn-blue/50 transition-colors"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        {done ? (
                            <div data-testid="checkout-order-success" className="text-center py-8">
                                <div className="w-16 h-16 mx-auto rounded-full bg-bn-green/10 border border-bn-green/40 flex items-center justify-center">
                                    <Check className="w-8 h-8 text-bn-green" />
                                </div>
                                <h3 className="mt-6 font-display font-bold text-2xl text-white">Order received</h3>
                                <p className="mt-3 text-sm text-slate-400 leading-relaxed max-w-sm mx-auto">
                                    Thank you, {form.name.split(" ")[0]}. We've received your message about the {plan.name} plan.
                                    Check your inbox at <span className="text-bn-cyan">{form.email}</span> — we'll reply with the payment details shortly.
                                </p>
                                <button
                                    data-testid="checkout-success-close-button"
                                    onClick={onClose}
                                    className="mt-8 px-8 py-3.5 rounded-full bg-bn-blue hover:bg-bn-bluehover text-white text-sm font-semibold tracking-wide transition-all"
                                >
                                    DONE
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="font-mono text-[10px] tracking-[0.25em] text-bn-blue font-semibold">SECURE ORDER</div>
                                <div className="mt-3 flex items-baseline justify-between gap-4">
                                    <h3 className="font-display font-extrabold text-2xl text-white">{plan.name}</h3>
                                    <div className="text-right">
                                        <div className="font-display font-extrabold text-2xl text-bn-blue">€{plan.price}</div>
                                        <div className="font-mono text-[10px] text-slate-500">{plan.per}</div>
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <div className="font-mono text-[10px] tracking-[0.25em] text-slate-500 mb-3">AVAILABLE PAYMENT METHODS</div>
                                    <div className="grid grid-cols-3 gap-2">
                                        {PAYMENT_METHODS.map((m) => (
                                            <div
                                                key={m.id}
                                                data-testid={`checkout-method-${m.id}`}
                                                className="rounded-lg border border-bn-line bg-bn-card/50 px-3 py-3 text-left opacity-80 select-none"
                                            >
                                                <div className="text-xs font-bold text-white leading-tight">{m.name}</div>
                                                <div className="mt-1 font-mono text-[9px] text-slate-500">{m.tag}</div>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="mt-3 text-xs text-slate-500 leading-relaxed flex items-start gap-2">
                                        <ShieldCheck className="w-3.5 h-3.5 text-bn-green shrink-0 mt-0.5" />
                                        Send us a message below — we'll reply by email with the payment details for your preferred method.
                                    </p>
                                </div>

                                <form onSubmit={confirm} className="mt-6">
                                    <div className="font-mono text-[10px] tracking-[0.25em] text-slate-500 mb-3">CONTACT US ABOUT THIS PLAN</div>
                                    <div className="grid sm:grid-cols-2 gap-3">
                                        <input
                                            data-testid="checkout-form-name"
                                            required
                                            minLength={2}
                                            maxLength={100}
                                            value={form.name}
                                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                                            placeholder="Your name"
                                            className={inputCls}
                                        />
                                        <input
                                            data-testid="checkout-form-email"
                                            required
                                            type="email"
                                            value={form.email}
                                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                                            placeholder="you@email.com"
                                            className={inputCls}
                                        />
                                    </div>
                                    <textarea
                                        data-testid="checkout-form-message"
                                        required
                                        minLength={10}
                                        maxLength={3000}
                                        rows={4}
                                        value={form.message}
                                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                                        placeholder={`Hi, I would like to subscribe to the ${plan.name} plan (€${plan.price})...`}
                                        className={`${inputCls} mt-3 resize-none`}
                                    />
                                    <button
                                        data-testid="checkout-confirm-order-button"
                                        type="submit"
                                        disabled={sending}
                                        className="group mt-5 w-full inline-flex items-center justify-center gap-2.5 py-4 rounded-full bg-bn-blue hover:bg-bn-bluehover disabled:opacity-60 text-white font-semibold text-sm tracking-wide transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,102,255,0.5)]"
                                    >
                                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 transition-transform group-hover:translate-x-1" />}
                                        {sending ? "SENDING..." : "SEND MESSAGE"}
                                    </button>
                                    <p className="mt-3 text-center font-mono text-[10px] text-slate-600">
                                        NO CARD DATA ON THIS SITE · DETAILS SENT BY EMAIL · 18+
                                    </p>
                                </form>
                            </>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
